import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Globe, Bell, Save, Loader2, CheckCircle2, AlertCircle,
  User as UserIcon, Sparkles, LifeBuoy, ChevronRight,
} from 'lucide-react'
import api from '../../lib/axios'
import { useAuthStore } from '../../stores/authStore'

const BTN_PRIMARY =
  'inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#006b5f] text-white text-sm font-semibold ' +
  'hover:bg-[#005a50] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'

const PREFS_KEY = 'tapio_settings'
const DEFAULT_PREFS = {
  emailLeads: true,
  emailOrders: true,
  emailNews: false,
}

const errMsg = (e) =>
  e?.response?.data?.message || 'Une erreur est survenue. Réessayez.'

function loadPrefs() {
  try {
    return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') }
  } catch {
    return DEFAULT_PREFS
  }
}

function Alert({ type, children }) {
  if (!children) return null
  const ok = type === 'success'
  const Icon = ok ? CheckCircle2 : AlertCircle
  return (
    <div
      className={`flex items-start gap-2 text-sm rounded-xl px-3 py-2 ${
        ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
      }`}
    >
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </div>
  )
}

function Card({ title, icon: Icon, children }) {
  return (
    <section className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 space-y-4">
      <h2 className="flex items-center gap-2 text-base font-semibold text-navy">
        <Icon className="w-5 h-5" /> {title}
      </h2>
      {children}
    </section>
  )
}

function Toggle({ checked, onChange, label, hint }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {hint && <p className="text-xs text-gray-500">{hint}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
          checked ? 'bg-[#006b5f]' : 'bg-gray-300'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  )
}

function LinkRow({ to, icon: Icon, title, hint }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
    >
      <div className="w-9 h-9 rounded-lg bg-[#006b5f]/10 text-[#006b5f] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800">{title}</p>
        <p className="text-xs text-gray-500 truncate">{hint}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400" />
    </Link>
  )
}

export default function Settings() {
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const [locale, setLocale] = useState('fr')
  const [langState, setLangState] = useState({ saving: false, ok: '', error: '' })

  const [prefs, setPrefs] = useState(loadPrefs)
  const [prefsOk, setPrefsOk] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .get('/profile')
      .then((r) => {
        if (cancelled) return
        setProfile(r.data.user)
        setLocale(r.data.user.locale || 'fr')
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  const saveLanguage = async (e) => {
    e.preventDefault()
    if (!profile) return
    setLangState({ saving: true, ok: '', error: '' })
    try {
      const { data } = await api.put('/profile', {
        name: profile.name,
        email: profile.email,
        phone: profile.phone || '',
        locale,
      })
      setProfile(data.user)
      try {
        useAuthStore.setState((s) => ({ user: { ...s.user, ...data.user } }))
      } catch { /* ignore */ }
      setLangState({ saving: false, ok: 'Langue enregistrée.', error: '' })
    } catch (err) {
      setLangState({ saving: false, ok: '', error: errMsg(err) })
    }
  }

  const updatePref = (key, value) => {
    const next = { ...prefs, [key]: value }
    setPrefs(next)
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(next))
      setPrefsOk('Préférences enregistrées sur cet appareil.')
      setTimeout(() => setPrefsOk(''), 2500)
    } catch { /* ignore */ }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-navy">Paramètres</h1>
        <p className="text-sm text-gray-500">Personnalisez votre espace Tapio.</p>
      </header>

      {/* Langue */}
      <Card title="Langue" icon={Globe}>
        <form onSubmit={saveLanguage} className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Langue de l'interface</span>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              disabled={!profile}
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#006b5f]/20 focus:border-[#006b5f]"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
          </label>

          <Alert type="success">{langState.ok}</Alert>
          <Alert type="error">{langState.error}</Alert>
          {!profile && <Alert type="error">Impossible de charger votre profil.</Alert>}

          <button type="submit" disabled={langState.saving || !profile} className={BTN_PRIMARY}>
            {langState.saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}{' '}
            Enregistrer
          </button>
        </form>
      </Card>

      {/* Notifications */}
      <Card title="Notifications" icon={Bell}>
        <div className="space-y-4">
          <Toggle
            label="Nouveaux contacts (leads)"
            hint="Un email à chaque nouveau contact sur vos pages."
            checked={prefs.emailLeads}
            onChange={(v) => updatePref('emailLeads', v)}
          />
          <Toggle
            label="Commandes et factures"
            hint="Suivi de vos commandes et de vos paiements."
            checked={prefs.emailOrders}
            onChange={(v) => updatePref('emailOrders', v)}
          />
          <Toggle
            label="Nouveautés Tapio"
            hint="Nouvelles fonctionnalités et conseils."
            checked={prefs.emailNews}
            onChange={(v) => updatePref('emailNews', v)}
          />
          <Alert type="success">{prefsOk}</Alert>
        </div>
      </Card>

      {/* Raccourcis */}
      <Card title="Mon compte" icon={UserIcon}>
        <div className="space-y-2">
          <LinkRow
            to="/app/profile"
            icon={UserIcon}
            title="Mon profil"
            hint="Nom, email, photo et mot de passe"
          />
          <LinkRow
            to="/app/subscription"
            icon={Sparkles}
            title="Abonnement"
            hint="Votre offre et votre facturation"
          />
          <LinkRow
            to="/app/support"
            icon={LifeBuoy}
            title="Aide & support"
            hint="Questions fréquentes et contact"
          />
        </div>
      </Card>
    </div>
  )
}